import { SESSION_REPLAY_NOTICE_VERSION } from "@backchannel/tax-contracts";
import { useEffect, useState } from "react";
import { useT } from "~/i18n";
import { getReplayConfig } from "~/lib/replay";
import "./session-replay-notice.css";

const NOTICE_KEY = "tax_session_replay_notice";

function readAcknowledged(): string | null {
  try {
    return localStorage.getItem(NOTICE_KEY);
  } catch {
    return null;
  }
}

function writeAcknowledged(version: string): void {
  try {
    localStorage.setItem(NOTICE_KEY, version);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Bottom-left, non-blocking notice when screen recording is on and the
 * operator has not yet acknowledged the current notice version.
 */
export function SessionReplayNotice() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    let id = 0;
    const check = () => {
      if (cancelled) return;
      const config = getReplayConfig();
      if (!config) return;
      // The recorder has its config: decide once and stop polling.
      window.clearInterval(id);
      if (!config.enabled) {
        setVisible(false);
        return;
      }
      const version = config.noticeVersion || SESSION_REPLAY_NOTICE_VERSION;
      const acknowledged = readAcknowledged();
      setDays(config.retentionDays);
      setVisible(acknowledged !== version);
    };
    id = window.setInterval(check, 2_000);
    check();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!visible) return null;

  const config = getReplayConfig();
  const version = config?.noticeVersion || SESSION_REPLAY_NOTICE_VERSION;

  return (
    <output className="session-replay-notice" aria-live="polite">
      <div className="session-replay-notice__body notice" data-tone="info">
        <span aria-hidden="true">ℹ</span>
        <div className="session-replay-notice__copy">
          <strong className="session-replay-notice__title">
            {t("sessionReplay.notice.title")}
          </strong>
          <p className="session-replay-notice__text mb-0">
            {t("sessionReplay.notice.body", { days: String(days) })}
          </p>
        </div>
      </div>
      <button
        type="button"
        className="btn btn--sm"
        onClick={() => {
          writeAcknowledged(version);
          setVisible(false);
        }}
      >
        {t("sessionReplay.notice.ack")}
      </button>
    </output>
  );
}
